import { Component, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { LazyLoadEvent, MenuItem } from 'primeng/api';
import { finalize } from 'rxjs/operators';
import { AppComponentBase, PagedTableSummary } from 'src/app/app-component-base';

import { AppComponent } from '../../../app.component';
import { SettingsClientService } from '../../../../shared/services/settingsclient.service';
import { AppSettingsClient } from '../../../../shared/AppSettingsClient';
import {
    PatientMedicationDeleteCommand,
    PatientMedicationForListResponse,
    PatientMedicationGetListQuery,
    PatientsServiceProxy
} from '../../../../shared/service-proxies/service-proxies';
import { EditPatientMedicationsComponent } from './editpatientmedications.component';
import { DateTimeService } from '../../../../shared/services/datetime.service';

@Component({
    templateUrl: './patients-medications.component.html',
    styleUrls: ['../../../../assets/css-custom/p-datatable-general.scss'],
    selector: 'app-pacientesmedicamentos-component'
})
export class PatientsMedicationsComponent extends AppComponentBase implements OnInit {

    @ViewChild('dt1', { static: false }) table;

    @Input()
        pacienteId: number;

    form: FormGroup;

    data: PatientMedicationForListResponse[];
    pagedTableSummary: PagedTableSummary = new PagedTableSummary();

    cols: any[];
    selectedItem: any;
    items: MenuItem[];
    query: PatientMedicationGetListQuery = new PatientMedicationGetListQuery();

    // browserStorageTableKey: string;
    // browserStorageTableFilterKey = 'table-patient-filters';

    permissions: any;

    constructor(
        injector: Injector,
        private formBuilder: FormBuilder,
        private router: Router,
        private service: PatientsServiceProxy,
        private app: AppComponent,
        private datetimeService: DateTimeService,
        private settingsClient: SettingsClientService
    ) {
        super(injector);
    }

    //   aconvenience getter for easy access to form fields
    get f() {
        return this.form.controls;
    }

    get dtService() {
        return this.datetimeService;
    }

    ngOnInit() {
        const self = this;

        self.permissions = {
            create: self.permission.isGranted('Catalogos.Patients.Edit'),
            edit: self.permission.isGranted('Catalogos.Patients.Edit')
        };

        // self.browserStorageTableKey = self.browserStorageService.calculateKey('table-patient');

        self.form = self.formBuilder.group({
            filterText: [null]
        });

        self.setColumns();
    }

    setColumns(): void {
        const self = this;

        self.cols = self.getDefaultColumns();
    }

    getDefaultColumns(): any[] {
        const self = this;

        return [
            { field: 'codigoMedicamento', header: this.l('Catalogos.Patients.Medications.Medication.Code.Form'), isActive: true },
            { field: 'medicamentoDesc', header: this.l('Catalogos.Patients.Medications.Medication.Form'), isActive: true },
            { field: 'periodo', header: this.l('Catalogos.Patients.Medications.Period.Form'), isActive: true },
            { field: 'fechaInicio', header: this.l('Catalogos.Patients.Medications.InitDate.Form'), isActive: true },
            { field: 'fechaFin', header: this.l('Catalogos.Patients.Medications.EndDate.Form'), isActive: true },
            { field: 'observaciones', header: this.l('Catalogos.Patients.Medications.Observations.Form'), isActive: true },
        ];
    }

    // configurateView(): void {
    //    const self = this;
    //    const callback = (response: any[]) => {
    //        if (response) {
    //            self.cols = response;
    //        }
    //    };

    //    self.app.configurateView(AppSettingsClient.ViewPatientsConfig, self.getDefaultColumns(), callback);
    // }

    filterSearch(event: any): void {
        const self = this;

        event.preventDefault();

        // self.browserStorageService.remove(self.browserStorageTableKey);
        self.table.reset();
        self.query.pageNumber = 1;
    }

    getList(): void {
        const self = this;

        self.query.filter = self.f.filterText.value;
        self.query.pacienteId = self.pacienteId;

        self.app.blocked = true;

        self.service.getPatientMedicationList(self.query)
            .pipe(finalize(() => {
                self.app.blocked = false;
            }))
            .subscribe(data => {
                self.data = data.items;

                // Calculate paged table summary
                self.pagedTableSummary = self.getPagedTableSummay(data.totalCount, self.query.pageNumber, self.query.pageSize);

                // Asigno filtros para que se queden guardados en localStorage
                // self.browserStorageService.set(self.browserStorageTableFilterKey, self.query);
            });
    }

    loadData(event: LazyLoadEvent) {
        const self = this;

        if (event.sortField) {
            self.query.sorting = event.sortField + ' ' + (event.sortOrder === 1 ? 'ASC' : 'DESC');
        } else {
            self.query.sorting = '';
        }

        self.query.pageNumber = 1 + (event.first / event.rows);
        self.query.pageSize = event.rows;

        self.getList();
    }

    create(): void {
        const self = this;

        const ref = self.dialogService.open(EditPatientMedicationsComponent, {
            styleClass: 'd-sm d-md-40 d-lg-40 d-xl-40',
            header: self.l('Catalogos.Patients.Medications.Create'),
            showHeader: true,
            dismissableMask: false,
            data: {
                patient: self.pacienteId
            }
        });

        ref.onClose.subscribe(() => {
            self.getList();
        });
    }

    edit(id): void {
        const self = this;

        const ref = self.dialogService.open(EditPatientMedicationsComponent, {
            styleClass: 'd-sm d-md-40 d-lg-40 d-xl-40',
            header: self.l('Catalogos.Patients.Medications.Edit'),
            showHeader: true,
            dismissableMask: false,
            data: {
                id: id
            }
        });

        ref.onClose.subscribe(() => {
            self.getList();
        });
    }

    delete(rowData: PatientMedicationForListResponse): void {
        const self = this;

        self.alertService.confirmDelete(
            self.l('Catalogos.Patients.Medications.ConfirmationDeleteMessage'),
            self.l('Confirmation'),
            function (resp) {

                if (!resp) {
                    return;
                }

                self.doDelete(rowData.id);
            }
        );
    }

    doDelete(id: number): void {
        const self = this;

        self.app.blocked = true;

        self.service.deletePatientMedication(new PatientMedicationDeleteCommand({ id: id }))
            .pipe(finalize(() => {
                self.app.blocked = false;
            }))
            .subscribe(data => {
                self.notify.success(self.l('Catalogos.Patients.Medications.SuccessfulDelete'), self.l('Success'));
                self.getList();
            });
    }
}
